import { ok, Result, fromPromise, err } from 'neverthrow';
import { Actor, Like, Post, Reply, Thread } from 'web-shared/src/types/bsky';
import {
  ErrorWithCode,
  InternalErrors,
  newError,
} from 'web-shared/src/types/errors';
import { match } from 'ts-pattern';

export const getPostThread = async (
  uri: string,
  likeLimit?: number,
): Promise<
  Result<
    {
      author: Actor;
      post: Post;
      likes: Like[];
      replies: Reply[];
    },
    ErrorWithCode<InternalErrors>
  >
> => {
  const dataResult = await fromPromise(
    Promise.all([
      fetch(
        `https://public.api.bsky.app/xrpc/app.bsky.feed.getPostThread?uri=${uri}`,
      ),
      fetch(
        `https://public.api.bsky.app/xrpc/app.bsky.feed.getLikes?limit=${likeLimit || 100}&uri=${uri}`,
      ),
    ]),
    (error) =>
      newError('FetchError', 'Unable to fetch thread or likes', { error }),
  );

  if (dataResult.isErr()) return err(dataResult.error);

  const [postRequest, likeRequest] = dataResult.value;

  const postResponse = await fromPromise<
    { thread: Thread },
    ErrorWithCode<InternalErrors>
  >(postRequest.json(), (error) =>
    newError('DecodeError', 'Failed to decode thread', {
      error,
      threadRequest: postRequest,
    }),
  );
  if (postResponse.isErr()) return err(postResponse.error);

  const likeResponse = await fromPromise<
    { likes: Like[] },
    ErrorWithCode<InternalErrors>
  >(likeRequest.json(), (error) =>
    newError('DecodeError', 'Failed to decode likes', {
      error,
      likeRequest,
    }),
  );
  if (likeResponse.isErr()) return err(likeResponse.error);

  const thread =
    'thread' in postResponse.value ? postResponse.value.thread : undefined;

  const likes =
    'likes' in likeResponse.value ? likeResponse.value.likes : undefined;

  if (!likes || !thread) {
    return err(
      newError('DecodeError', 'Missing thread or likes from payload', {
        likeResponse,
        threadResponse: postResponse,
      }),
    );
  }

  const replies = 'replies' in thread ? thread.replies : undefined;

  return ok({
    author: thread.post.author,
    post: thread.post,
    likes: likes,
    replies: replies || [],
  });
};

export const postToPlainText = (postRecord: Post): string => {
  let postText = postRecord.record.text;
  if (postRecord.record.facets) {
    postRecord.record.facets.forEach((facet) => {
      console.log('Handing Facets', facet);

      const index = facet.index;
      const feature = match(facet.features[0])
        .with({ $type: 'app.bsky.richtext.facet#link' }, (f) => f.uri)
        .with({ $type: 'app.bsky.richtext.facet#tag' }, (f) => `#${f.tag}`)
        .exhaustive();

      postText =
        postText.substring(0, index.byteStart) +
        feature +
        postText.substring(index.byteEnd);
    });
  }

  // This could be way nicer lmao
  return postText;
};
